import { useMemo } from 'react'
import { Navigate, useLocation, useRoutes } from 'react-router-dom'

import { prepareNavigationProps } from '~/app/router/prepareNavigationProps'
import Download from '~/pages/download'
import Login from '~/pages/login'
import { SubscriptionPage } from '~/pages/subscription'
import UpsellPage from '~/pages/upsell'
import { PageId } from '~/shared/constants/pagesConstants'
import { usePageInfoContext } from '~/shared/contexts/PageInfoContext'
import { getPathFromPageId } from '~/shared/helpers/getPathFromPageId'
import { useBulkSelector } from '~/shared/hooks/useBulkSelector'
import {
  selectCohortID,
  selectCurrentVariantSteps,
} from '~/shared/store/common/selects'
import { selectUserStatus, selectUserUUID } from '~/shared/store/user/selects'
import { IStep } from '~/shared/types/variantTypes'

import { useAvailableFunnelRoute } from '../hooks/useAvailableFunnelRoute'
import { OnboardingLayout } from '../layouts/OnboardingLayout'
import { ProtectedLayout } from '../layouts/ProtectedLayout'
import { getPageMapByCohort } from './getPageMapByCohort'

const useSelectors = () =>
  useBulkSelector({
    steps: selectCurrentVariantSteps,
    cohort: selectCohortID,
    uuid: selectUserUUID,
    userStatus: selectUserStatus,
  })

export const RouteList = () => {
  const { search } = useLocation()
  const { steps, cohort, uuid, userStatus } = useSelectors()

  const availableRoute = useAvailableFunnelRoute()
  const { currentSubscriptionPageId, currentUpsellPageId } =
    usePageInfoContext()

  const commonNavigationProps = useMemo(
    () => ({
      uuid,
      cohort,
      search,
      availableRoute,
    }),
    [uuid, cohort, search, availableRoute],
  )

  const firstPagePath = useMemo(
    () =>
      getPathFromPageId({
        pageId: steps[0].id,
        cohort,
        uuid,
        currentSearch: search,
      }),
    [cohort, search, steps, uuid],
  )

  const pageMap = useMemo(() => getPageMapByCohort(cohort), [cohort])

  const { onboardingPages, subscriptionPages, upsellPages } = useMemo(() => {
    return steps.reduce<{
      onboardingPages: IStep[]
      subscriptionPages: IStep[]
      upsellPages: IStep[]
    }>(
      (accum, { isSubscriptions, isPayment, isUpsell }, ind, arr) => {
        if (isSubscriptions || isPayment) {
          accum.subscriptionPages.push(arr[ind])
          return accum
        }

        if (isUpsell) {
          accum.upsellPages.push(arr[ind])
          return accum
        }

        accum.onboardingPages.push(arr[ind])
        return accum
      },
      {
        onboardingPages: [],
        subscriptionPages: [],
        upsellPages: [],
      },
    )
  }, [steps])

  return useRoutes([
    { index: true, element: <Navigate to={firstPagePath} /> },
    {
      element: <OnboardingLayout />,
      children: onboardingPages.map(({ id: pageId }, index, arr) => {
        const CurrentPage = pageMap[pageId]

        const navigationProps = prepareNavigationProps({
          ...commonNavigationProps,
          currentPageIndex: index,
          pages: arr,
        })

        // `onboardingPages` ends at `proof-*`, while `subscription-*` lives in a different slice.
        // Because of that, for the last onboarding step (`proof-9`) `nextPagePath` from
        // `onboardingPages` is empty. Here we derive the next step from the full `steps` list
        // to follow the actual flow (without hardcoded routes).
        const fullPageIndex = steps.findIndex((step) => step.id === pageId)
        const nextFullStep =
          fullPageIndex >= 0 ? steps[fullPageIndex + 1] : undefined
        const nextFullPagePath = nextFullStep
          ? getPathFromPageId({
              pageId: nextFullStep.id,
              cohort,
              uuid,
              currentSearch: search,
            })
          : ''

        // Fallback: if there is no immediate next step in `steps` (rare, but can
        // happen for some funnels), go to the first subscription/payment step.
        const firstSubscriptionStep = steps.find(
          (step) => step.isSubscriptions || step.isPayment,
        )
        const firstSubscriptionPagePath = firstSubscriptionStep
          ? getPathFromPageId({
              pageId: firstSubscriptionStep.id,
              cohort,
              uuid,
              currentSearch: search,
            })
          : ''

        const props = {
          pageId,
          ...navigationProps,
          nextPagePath:
            nextFullPagePath ||
            navigationProps.nextPagePath ||
            firstSubscriptionPagePath,
        }

        return {
          path: pageId,
          element: (
            <ProtectedLayout isAvailable={!availableRoute}>
              <CurrentPage key={pageId} {...props} />
            </ProtectedLayout>
          ),
        }
      }),
    },
    {
      element: <OnboardingLayout />,
      children: subscriptionPages.map(({ id: pageId }, index, arr) => {
        const isAvailable =
          (!!userStatus?.email.hasEmail && !userStatus.hasSubscription) ||
          availableRoute === currentSubscriptionPageId
        const navigationProps = prepareNavigationProps({
          ...commonNavigationProps,
          currentPageIndex: index,
          pages: arr,
        })
        const props = { pageId, ...navigationProps }

        return {
          path: pageId,
          element: (
            <ProtectedLayout isAvailable={isAvailable}>
              <SubscriptionPage {...props} />
            </ProtectedLayout>
          ),
        }
      }),
    },
    {
      element: <OnboardingLayout />,
      children: upsellPages.map(({ id: pageId }, index, arr) => {
        const { prevPagePath, nextPagePath } = prepareNavigationProps({
          ...commonNavigationProps,
          currentPageIndex: index,
          pages: arr,
        })

        return {
          path: pageId,
          element: (
            <ProtectedLayout
              isAvailable={availableRoute === currentUpsellPageId}
            >
              <UpsellPage
                pageId={pageId}
                nextPagePath={nextPagePath}
                prevPagePath={prevPagePath}
                alternativePagePath="/"
              />
            </ProtectedLayout>
          ),
        }
      }),
    },
    {
      path: PageId.LOGIN,
      element: (
        <ProtectedLayout
          isAvailable={
            availableRoute === PageId.LOGIN ||
            availableRoute === currentUpsellPageId
          }
        >
          <Login />
        </ProtectedLayout>
      ),
    },
    {
      path: PageId.DOWNLOAD,
      element: (
        <ProtectedLayout
          isAvailable={
            availableRoute === PageId.DOWNLOAD ||
            availableRoute === currentUpsellPageId
          }
        >
          <Download />
        </ProtectedLayout>
      ),
    },
    {
      path: '*',
      element: <Navigate to={firstPagePath} />,
    },
  ])
}
